import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';

/** The Album keeps the newest 30 photos (plan §3.10); the UI warns before the oldest is replaced. */
export const ALBUM_LIMIT = 30;
/** At most this many stickers on one photo (ux-screens §3.11). */
export const MAX_STICKERS = 10;

/**
 * A photo was taken (content-schemas §13 `takePhoto`): its pixels are already in the `photos` table; the save
 * only keeps the id list. When the Album is full the oldest photo makes room (PHOTO_DELETED → blob removed).
 */
export function takePhoto(draft: GameSave, cmd: CommandOf<'takePhoto'>): CommandResult {
  if (!draft.features.includes('photoMode')) return { ok: false, error: 'LOCKED' };
  if (draft.album.photoIds.includes(cmd.photoId)) return { ok: false, error: 'ALREADY_CLAIMED' };
  if (cmd.frameId && !draft.cosmetics.photoFrames.includes(cmd.frameId))
    return { ok: false, error: 'INVALID_ARGUMENT', detail: { frameId: cmd.frameId } };
  if (
    cmd.stickerIds.length > MAX_STICKERS ||
    cmd.stickerIds.some((id) => !draft.cosmetics.stickers.includes(id))
  )
    return { ok: false, error: 'INVALID_ARGUMENT', detail: { stickers: cmd.stickerIds.length } };
  const events: DomainEvent[] = [];
  while (draft.album.photoIds.length >= ALBUM_LIMIT) {
    const oldest = draft.album.photoIds.shift();
    if (oldest) events.push({ type: 'PHOTO_DELETED', photoId: oldest });
  }
  draft.album.photoIds.push(cmd.photoId);
  const roomId = cmd.sceneId === 'townmap' ? 'home' : cmd.sceneId;
  events.push({
    type: 'PHOTO_TAKEN',
    photoId: cmd.photoId,
    sceneId: cmd.sceneId,
    cozinessLevel: draft.coziness[roomId].bestLevel,
    ...(cmd.frameId ? { frameId: cmd.frameId } : {}),
    stickerCount: cmd.stickerIds.length,
  });
  return { ok: true, events };
}

export function deletePhoto(draft: GameSave, cmd: CommandOf<'deletePhoto'>): CommandResult {
  if (!draft.album.photoIds.includes(cmd.photoId)) return { ok: false, error: 'NOT_FOUND' };
  draft.album.photoIds = draft.album.photoIds.filter((id) => id !== cmd.photoId);
  return { ok: true, events: [{ type: 'PHOTO_DELETED', photoId: cmd.photoId }] };
}
